// Lageret for ULL — lagets situasjonsbilde i minnet.
//
// MEKANIKKEN BOR IKKE HER. Den uforanderlige tilstandsmaskinen (dedup, stabil
// sortering, «kast aldri en observasjon fra en ukjent sensor», borte-logg,
// «broen overskriver ikke operatørens flagg») ligger i `@skade/sensorflate`,
// fordi den er identisk for hver sensorflate. Denne fila er ULLs BESTILLING:
// jegertroppens seks sensorarter, deres rekkefølge, hva et nøytralt flagg er, og
// oversettelsen fra UGS-kontraktens wire-meldinger til flatens ordforråd.
//
// ══ REKKEFØLGEN PÅ SENSORSTOLPEN, OG HVORFOR DEN ER SLIK ═══════════════════
// Stolpen står FAST og sorteres aldri på status — jegeren skal kunne strekke
// hånda mot en rad i mørket uten å lese. Rekkefølgen er derfor et OPERATIVT valg,
// og valget er: DEN SOM OFTEST UTLØSER FØRST STÅR ØVERST.
//
// Begrunnelsen er fysisk og kan etterprøves mot kontraktens egen
// `TYPISK_DEKNING_M_UVERIFISERT`: det er rekkevidden som avgjør hvem som merker
// et mål først når det nærmer seg. Akustisk (60 m) hører skritt lenge før
// geofonen (25 m) kjenner dem; PIR (15 m) og kamera (20 m) krever at målet er
// nesten framme; brytertråd (8 m) og magnetometer (5 m) utløser først når målet
// er PÅ dem. Rekkefølgen i stolpen blir dermed omtrent den KRONOLOGISKE
// rekkefølgen utløsningene kommer i, og øyet leser stolpen ovenfra og ned som en
// tidslinje.
//
// Kontraktens egen `UgsArt`-rekkefølge er en annen («seismisk og PIR er
// arbeidshestene, kamera er det eneste et menneske kan verifisere») — den
// rangerer etter HVOR MYE arten brukes, ikke etter hvem som varsler først. Begge
// er riktige for sitt formål, og det er nettopp derfor rekkefølgen er en
// parameter til `lagLagermotor` og ikke en konstant i pakken.
//
// Magnetometeret står SIST og ikke nest sist, selv om brytertråden har kortere
// rekkevidde: en tråd sier «noe brøt den her, nå», mens et magnetometer bare sier
// «jern i bevegelse» — den er den svakeste raden på skjermen, og den skal ikke
// stå over noe som er sterkere.
import {
  lagLagermotor, sensorStatus, type Flatemelding, type Flatetilstand, type SensorStatus,
} from '@skade/sensorflate';
import type { UgsArt, UgsSensor } from '@skade/contracts';
import {
  ULL_TERSKLER, ugsTilSensorrad, utlosningTilHendelse,
  type Rute, type Sensorrad, type Ullflagg, type Ullhendelse, type Ulllag, type Ullobservasjon, type UllMelding,
} from '../api/typer.js';

/** Stolpens faste rekkefølge. Se filens toppkommentar for begrunnelsen. */
export const SENSORREKKEFOLGE = [
  'akustisk',
  'seismisk',
  'pir',
  'kamera',
  'brytertråd',
  'magnetisk',
] as const satisfies readonly UgsArt[];

export type Lagertilstand = Flatetilstand<Sensorrad, Ulllag, Ullhendelse>;

const motor = lagLagermotor<UgsArt, Sensorrad, Ulllag, Ullobservasjon, Ullhendelse>({
  rekkefolge: SENSORREKKEFOLGE,
  noytraltFlagg: 'ingen' satisfies Ullflagg,
  /**
   * En sensor vi bare kjenner gjennom en utløsning. Den forkastes ALDRI — en
   * utløsning fra en node broen ikke har annonsert er nettopp noe jegeren må få
   * vite om, for det betyr enten en feilkonfigurert node eller en node noen
   * andre har lagt ut.
   *
   * MERK artsvalget: `UgsArt` er en lukket union uten «annet»-verdi, så noe MÅ
   * velges, og valget er `seismisk` fordi den PÅSTÅR MINST. En seismisk sensor er
   * rundstrålende (ingen retning), har ingen bilder og ingen lydklasse — altså
   * finnes det ingen nedstrøms kode som kan lure seg til å tro at denne noden vet
   * hvilken vei noe gikk. Hadde vi valgt `pir`, ville en retningsutløsning fra
   * den blitt lest som gyldig.
   */
  ukjentSensor: (sensorId) => ({
    id: sensorId,
    art: 'seismisk',
    navn: sensorId,
    tilstand: 'ukjent',
    merknad:
      'Noden er IKKE annonsert av broen — vi kjenner bare utløsningene. Arten er ukjent og føres som seismisk fordi det er '
      + 'antakelsen som påstår minst (rundstrålende, ingen retning). Posisjonen er ukjent, så utløsningene kan ikke brukes i sekvensanalyse',
    ugs: null,
  }),
});

export function tomtLager(): Lagertilstand {
  return motor.tomtLager();
}

/**
 * Bruk én wire-melding på tilstanden. Returnerer NY tilstand (aldri mutert inn).
 *
 * Dette er oversetterlaget: UGS-kontraktens fem meldingstyper har ikke samme
 * form som lagermotorens, og forskjellen er ikke tilfeldig. Kontrakten melder
 * DELTAER (`status` med bare batteri, `fjernet` med bare et tidspunkt), mens
 * motoren tar imot HELE sensoren. Sammenslåingen skjer derfor her, mot den
 * sensoren vi allerede har — og finnes den ikke, forkastes deltaet framfor å
 * bygge en halv sensor av det.
 *
 * ══ ØVINGSSTEMPELET FRA KONVOLUTTEN NED PÅ ENTITETEN ══════════════════════
 * `m.ovelse` er satt av øvingskilden (`lagOvelseskilde` i `@skade/sensorflate` —
 * den ene utgangen), aldri av broen: kontrakten har helt riktig ikke noe
 * øvingsfelt, og `tolkMelding` leser det ikke fra wire. Det er klienten som vet
 * om den mater seg selv.
 *
 * Stempelet må FØLGE MED NED på sensorraden og hendelsen, ikke stoppe på
 * konvolutten. Grunnen er konkret: et sekvenskort eller en sensorrad vises alene
 * i et poppet vindu og klippes ut i en rapport, og et umerket utsnitt er nettopp
 * det §1 forbyr.
 *
 * MERK `status`/`fjernet`: de er DELTAER mot en sensor vi allerede har, og
 * øvingsflagget hentes derfra (`fins.ovelse`) og ikke fra deltaet. En ekte bro
 * skal ikke kunne «vaske» en øvingsnode ved å sende et batteritall for den — og
 * en øvingspuls skal ikke kunne merke en ekte node.
 */
export function bruk(t: Lagertilstand, m: UllMelding): Lagertilstand {
  const ovelse = m.ovelse === true;
  switch (m.type) {
    case 'lag':
      return motor.bruk(t, {
        type: 'post',
        post: ovelse ? { ...m.lag, ovelse: true } : m.lag,
      });

    case 'sensor':
      return motor.bruk(t, { type: 'sensor', sensor: ugsTilSensorrad(m.sensor, ovelse) });

    case 'utlosning':
      // Én utløsning = én «kontakt» i flatens forstand. Se toppkommentaren i
      // `api/typer.ts` for hvorfor.
      return motor.bruk(t, { type: 'kontakt', kontakt: utlosningTilHendelse(m.utlosning, ovelse) });

    case 'status': {
      const fins = t.sensorer.find((s) => s.id === m.sensorId);
      if (!fins || !fins.ugs) return t;
      const ugs = {
        ...fins.ugs,
        ...(m.batteri ? { batteri: m.batteri } : {}),
        ...(m.lenke ? { lenke: m.lenke } : {}),
      };
      return motor.bruk(t, { type: 'sensor', sensor: ugsTilSensorrad(ugs, fins.ovelse ?? false) });
    }

    case 'fjernet': {
      const fins = t.sensorer.find((s) => s.id === m.sensorId);
      if (!fins || !fins.ugs) return t;
      // `aktiv: false` + `fjernetMs` er det kontrakten krever av en hentet node,
      // og det er også det `dekningshull()` leser for å UTELATE den. Uten dette
      // ville analysen regnet med dekning som ligger i sekken.
      const ugs = {
        ...fins.ugs,
        aktiv: false,
        fjernetMs: m.tsMs,
        merknad: `Hentet inn${m.av ? ` av ${m.av}` : ''}${fins.ugs.merknad ? ` · ${fins.ugs.merknad}` : ''}`,
      };
      return motor.bruk(t, { type: 'sensor', sensor: ugsTilSensorrad(ugs, fins.ovelse ?? false) });
    }

    case 'borte':
      return motor.bruk(t, { type: 'sensor-borte', sensorId: m.sensorId, arsak: m.arsak });

    case 'rute':
      // Ruter er PLANDATA, ikke sensorstrøm, og `Flatetilstand` har ingen plass
      // for dem. De håndteres av `brukRute` og holdes ved siden av lageret i
      // `main.ts`. (Se rapporten: et generisk `plan`-felt i sensorflate ville
      // fjernet dette ene skjøtet.)
      return t;
  }
}

/**
 * Rutene, som egen uforanderlig liste. Samme regler som lageret: dedup på id,
 * nyeste melding vinner, stabil sortering på navn.
 *
 * `ovelse` bæres inn av kalleren (`main.ts` leser `m.ovelse`) og stemples her, av
 * samme grunn som i `bruk`: en akse er PLANDATA, den vises i dekningspanelet med
 * navn og kilde, og en oppdiktet akse som ser ekte ut er et grunnlag ingen kan
 * etterprøve.
 */
export function brukRute(ruter: readonly Rute[], rute: Rute, ovelse = false): Rute[] {
  const merket = ovelse ? { ...rute, ovelse: true as const } : rute;
  const uten = ruter.filter((r) => r.id !== merket.id);
  return [...uten, merket].sort((a, b) => a.navn.localeCompare(b.navn, 'no') || a.id.localeCompare(b.id, 'no'));
}

/** Jegerens flagg — den ene endringen som kommer fra klienten, ikke broen. */
export function settFlagg(t: Lagertilstand, utlosningId: string, flagg: Ullflagg): Lagertilstand {
  return motor.settFlagg(t, utlosningId, flagg);
}

/** Oppslag sensorId → art, som sekvens- og dekningslogikken trenger. */
export function artOppslag(t: Lagertilstand): (sensorId: string) => UgsArt | undefined {
  return motor.artOppslag(t);
}

/** Sammenligneren stolpen sorteres med. Eksponert for appens egne lister. */
export const sorterSensorer = motor.sorterSensorer;

/**
 * SENSORSTATUS FOR EN UGS — og hvorfor den ikke er `sisteMaalingPerSensor`.
 *
 * Dette er det viktigste stedet ULL avviker fra NJORD, og avviket er
 * OBLIGATORISK. Friskhetsmodellen i pakken svarer på «hvor gammel er målingen»,
 * og for en radar som snurrer er det det rette spørsmålet. For en UGS er det
 * FEIL: en geofon som ikke har utløst på en time er ikke stille — det har bare
 * ikke gått noen der, og det er hele hensikten med den. Ville vi vist siste
 * utløsning som «friskhet», ville hver eneste velfungerende node i skogen sett
 * død ut, og da slutter jegeren å lese stolpen.
 *
 * Livstegnet er derfor `lenke.sistHortMs` — hjertebanken kontrakten har et eget
 * felt for, med nettopp denne begrunnelsen («skiller stille fordi ingen har gått
 * der fra stille fordi den er død»). Alderen på siste UTLØSNING vises for seg
 * selv, i sin egen kolonne, og aldri som helse.
 *
 * Melder broen ingen `sistHortMs`, er svaret `null` = «har ikke meldt et eneste
 * livstegn», og pakken graderer den som nede. Det er riktig: da vet vi ingenting
 * om noden.
 */
export function sensorStatuser(t: Lagertilstand, naaMs: number): SensorStatus[] {
  return t.sensorer.map((s) =>
    sensorStatus(s, s.ugs?.lenke.sistHortMs ?? null, naaMs, ULL_TERSKLER));
}

/** Tidspunktet for siste utløsning på en sensor, eller `null`. */
export function sisteUtlosningFor(t: Lagertilstand, sensorId: string): number | null {
  return t.sisteMaalingPerSensor[sensorId] ?? null;
}

/** Utløsningene i tidsrekkefølge, eldste først. Grunnlaget for sekvensanalysen. */
export function hendelserITid(t: Lagertilstand): Ullhendelse[] {
  return [...t.kontakter].sort(
    (a, b) => a.utlosning.tidspunktMs - b.utlosning.tidspunktMs || a.id.localeCompare(b.id, 'no'),
  );
}

/**
 * Sensorene som kontraktens funksjoner kan regne på — altså de vi har en EKTE
 * `UgsSensor` for. En node vi bare kjenner gjennom utløsninger har ingen
 * posisjon, og en sekvensanalyse på en ukjent posisjon ville vært et tall uten
 * grunnlag. De utelates derfor fra beregningene, men ALDRI fra stolpen.
 */
export function kjenteSensorer(t: Lagertilstand): UgsSensor[] {
  return t.sensorer.map((s) => s.ugs).filter((u): u is UgsSensor => u !== null);
}

/** Motorens meldingstype, eksponert for testene. */
export type Flatemeldingen = Flatemelding<Sensorrad, Ulllag, Ullobservasjon, Ullhendelse>;
